import { useMemo } from 'react'

import { Table } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import { useVehicleAnalyticsStore } from '@/store/vehicleAnalytics.store'
import type { CostCategory } from '@/types/vehicleAnalytics'
import { formatPLN } from '@/utils/currency'

interface DriverRow extends Record<CostCategory, number> {
  driverId: string;
  driverName: string;
  reports: Set<string>;
  total: number;
}

export function DriversBreakdown() {
  const { t } = useTranslation()
  const entries = useVehicleAnalyticsStore(s => s.entries)

  const rows = useMemo(() => {
    const byDriver = new Map<string, DriverRow>()
    entries.forEach(entry => {
      const row = byDriver.get(entry.driverId) ?? {
        driverId: entry.driverId,
        driverName: entry.driverName ?? '—',
        reports: new Set<string>(),
        advance: 0,
        paidRoads: 0,
        fuel: 0,
        total: 0,
      }
      row.reports.add(entry.reportId)
      row[entry.category] += entry.amountPLN
      row.total += entry.amountPLN
      byDriver.set(entry.driverId, row)
    })
    return Array.from(byDriver.values()).sort((a, b) => b.total - a.total)
  }, [entries])

  return (
    <Table>
      <Table.ScrollContainer>
        <Table.Content aria-label={t('vehicle_analytics.drivers')} className="min-w-[640px]">
          <Table.Header>
            <Table.Column isRowHeader>{t('reports.driver')}</Table.Column>
            <Table.Column>{t('vehicle_analytics.reports')}</Table.Column>
            <Table.Column>{t('vehicle_analytics.categories.advance')}</Table.Column>
            <Table.Column>{t('vehicle_analytics.categories.paidRoads')}</Table.Column>
            <Table.Column>{t('vehicle_analytics.categories.fuel')}</Table.Column>
            <Table.Column>{t('vehicle_analytics.total')}</Table.Column>
          </Table.Header>
          <Table.Body>
            {rows.map(row => (
              <Table.Row key={row.driverId || 'unknown'}>
                <Table.Cell className="font-medium">{row.driverName}</Table.Cell>
                <Table.Cell>{row.reports.size}</Table.Cell>
                <Table.Cell className="tabular-nums">{formatPLN(row.advance)}</Table.Cell>
                <Table.Cell className="tabular-nums">{formatPLN(row.paidRoads)}</Table.Cell>
                <Table.Cell className="tabular-nums">{formatPLN(row.fuel)}</Table.Cell>
                <Table.Cell className="font-bold tabular-nums">{formatPLN(row.total)} PLN</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
    </Table>
  )
}
